/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { type ComponentProps } from 'react';
import Picture from 'components/Picture/Picture';
import { useImageHeight } from '../effects';
import useAnimation from './animations';
import './LargeImage.scss';

const DEFAULT_IMAGE: ComponentProps<typeof Picture> = {};

interface LargeImageProps {
  enhanced?: boolean;
  image?: ComponentProps<typeof Picture>;
  heading?: string;
  excerpt?: string;
  author?: string;
  photographer?: string;
}

/**
 * This is the LargeImage component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function LargeImage({
  enhanced = false,
  image = DEFAULT_IMAGE,
  heading = '',
  excerpt = '',
  author = '',
  photographer = '',
}: LargeImageProps) {
  const [ref] = useAnimation();
  const imageRef = useImageHeight<HTMLDivElement>();

  function renderHeading() {
    const lines = heading.replace(/[\n\r]/g, '').split(/<br\s*[/]?>/gi);
    return lines.map((line, i) => <span key={i}>{line}</span>);
  }

  function renderPhotographer() {
    if (!photographer) return null;

    return (
      <>
        {' // '}
        <span className="photo">{photographer}</span>
      </>
    );
  }

  function renderExcerpt() {
    if (!enhanced) return null;

    return (
      <div className="text">
        <div>
          <div key="excerpt" className="excerpt">
            <p dangerouslySetInnerHTML={{ __html: excerpt }} />
          </div>
          <div key="copyright" className="copyright">
            <span className="author">{author}</span>
            {renderPhotographer()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <header ref={ref} className="header large-image">
      <div className="picture-heading">
        <div ref={imageRef} className="image">
          <div>
            <Picture {...image} />
          </div>
        </div>
        <h1>{renderHeading()}</h1>
      </div>

      {renderExcerpt()}
    </header>
  );
}

export default LargeImage;
